/**
 * useValidatePaymentSelectState()
 *
 * Validates paymentSelect state is complete
 * If not, set error message and navigate to previous step
 *
 * Used by steps of donate journeys that expect a complete paymentSelect state
 * ...to prevent them being accessed in the wrong order (e.g. browser forward/back or copying/pasting an old URL)
 */
import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { setErrorMessage } from '@/features/paymentProcess/transactionSlice';

import { PaymentSelectState } from './paymentSelectSlice';

export const validateState = (
  paymentSelectState: PaymentSelectState,
  journey?: string,
  requireProvider = false
): boolean => {
  // Define required fields based on journey
  const paymentSelectKeys: Partial<keyof PaymentSelectState>[] = [
    'orderReference',
    'amount',
    'currency',
  ];

  // Only require provider where it's actually needed (the card-payment step),
  // and never for Payin journeys. Other steps (e.g. user/shipping details)
  // don't use the provider, and it is intentionally cleared on arrival at
  // payment-select, which can result in a spurious error, see bug documented
  // on PR #340.
  if (requireProvider && journey !== 'payin') {
    paymentSelectKeys.push('provider');
  }

  return paymentSelectKeys.every(key => (paymentSelectState[key]));
};

export function useValidatePaymentSelectState(requireProvider = false) {
  // Takes value from state once (on load), do not re-render on state change (using `() => true` equality check as second argument)
  const paymentSelectState = useAppSelector(state => state.paymentSelect, () => true);
  const journey = useAppSelector(state => state.journeys.journey);
  const dispatch = useAppDispatch();

  const journeyNavigate = useJourneyNavigate();

  useEffect(() => {
    // If the required fields are present (as expected), do nothing
    if (validateState(paymentSelectState, journey, requireProvider)) {
      return;
    }

    dispatch(setErrorMessage('You must complete this step before you can proceed.'));
    journeyNavigate('back');
  }, [paymentSelectState, journey, requireProvider, dispatch, journeyNavigate]);
}
